import * as React from "react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { Database } from "lucide-react";

// ─────────────────────────────────────────────────────────────
// Base Table Components
// ─────────────────────────────────────────────────────────────

const Table = React.forwardRef(({ className, ...props }, ref) => (
  <div className="relative w-full overflow-auto">
    <table
      ref={ref}
      className={cn("w-full caption-bottom text-sm", className)}
      {...props}
    />
  </div>
));

Table.displayName = "Table";

const TableHeader = React.forwardRef(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />
));

TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props}
  />
));

TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
      className,
    )}
    {...props}
  />
));

TableFooter.displayName = "TableFooter";

const TableRow = React.forwardRef(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
      className,
    )}
    {...props}
  />
));

TableRow.displayName = "TableRow";

const TableHead = React.forwardRef(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-11 px-4 text-left align-middle text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap",
      className,
    )}
    {...props}
  />
));

TableHead.displayName = "TableHead";

const TableCell = React.forwardRef(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn("p-4 align-middle text-sm", className)}
    {...props}
  />
));

TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props}
  />
));

TableCaption.displayName = "TableCaption";

// ─────────────────────────────────────────────────────────────
// DataTable
// ─────────────────────────────────────────────────────────────

const DEFAULT_COLUMN_WIDTH = 180;

const DataTable = ({
  data = [],
  columns = [],
  loading = false,

  htrClass,
  trClass,
  thClass,
  tdClass,

  ExpandedRow,

  emptyText = "No record found",
  isAplyingFilter = false,
  resetFilters = () => {},

  Icon = Database,
  subText = "Try adjusting your search or filter criteria",

  boxClass = "",
  maxHeight,
  onScroll = () => {},
}) => {
  const isEmpty = !data?.length;
  const showTable = !isEmpty || loading;

  // ─────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────

  const getColumnWidth = React.useCallback((col) => {
    if (typeof col.width === "number") return col.width;

    if (typeof col.width === "string") {
      return parseInt(col.width, 10);
    }

    if (typeof col.minWidth === "number") {
      return col.minWidth;
    }

    return DEFAULT_COLUMN_WIDTH;
  }, []);

  // Check if any column has width config
  const hasFixedColumns = React.useMemo(() => {
    return columns.some((col) => col.width || col.minWidth);
  }, [columns]);

  // Table minimum width
  const tableMinWidth = React.useMemo(() => {
    if (!hasFixedColumns) return "100%";

    return columns.reduce((acc, col) => {
      return acc + getColumnWidth(col);
    }, 0);
  }, [columns, hasFixedColumns, getColumnWidth]);

  // ─────────────────────────────────────────────────────────────
  // Sticky LEFT offset
  // ─────────────────────────────────────────────────────────────

  const getLeftStickyOffset = React.useCallback(
    (index) => {
      let offset = 0;

      for (let i = 0; i < index; i++) {
        const col = columns[i];

        if (col.sticky === "left") {
          offset += getColumnWidth(col);
        }
      }

      return offset;
    },
    [columns, getColumnWidth],
  );

  // ─────────────────────────────────────────────────────────────
  // Sticky RIGHT offset
  // ─────────────────────────────────────────────────────────────

  const getRightStickyOffset = React.useCallback(
    (index) => {
      let offset = 0;

      for (let i = columns.length - 1; i > index; i--) {
        const col = columns[i];

        if (col.sticky === "right") {
          offset += getColumnWidth(col);
        }
      }

      return offset;
    },
    [columns, getColumnWidth],
  );

  // ─────────────────────────────────────────────────────────────
  // Sticky styles generator
  // ─────────────────────────────────────────────────────────────

  const getStickyStyles = React.useCallback(
    (col, index, isHeader = false) => {
      if (!col.sticky) return {};

      const commonStyles = {
        position: "sticky",
        zIndex: isHeader ? 40 : 30,
      };

      if (col.sticky === "left") {
        return {
          ...commonStyles,
          left: `${getLeftStickyOffset(index)}px`,
        };
      }

      if (col.sticky === "right") {
        return {
          ...commonStyles,
          right: `${getRightStickyOffset(index)}px`,
        };
      }

      return {};
    },
    [getLeftStickyOffset, getRightStickyOffset],
  );

  return (
    <div
      className={cn(
        "w-full overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900",
        boxClass,
      )}
    >
      {/* Horizontal Scroll */}
      <div className="relative w-full overflow-x-auto" onScroll={onScroll}>
        {/* Vertical Scroll */}
        <div
          style={{
            maxHeight: maxHeight || undefined,
            overflowY: maxHeight ? "auto" : undefined,
          }}
        >
          <table
            className="border-collapse"
            style={{
              width: "100%",
              minWidth:
                hasFixedColumns && typeof tableMinWidth === "number"
                  ? `${tableMinWidth}px`
                  : "100%",
              tableLayout: hasFixedColumns ? "fixed" : "auto",
            }}
          >
            {/* Column Widths */}
            {hasFixedColumns && (
              <colgroup>
                {columns.map((col) => (
                  <col
                    key={col.key}
                    style={{
                      width:
                        typeof col.width === "number"
                          ? `${col.width}px`
                          : col.width ||
                            `${col.minWidth || DEFAULT_COLUMN_WIDTH}px`,
                    }}
                  />
                ))}
              </colgroup>
            )}

            {/* Header */}
            <thead>
              <tr
                className={cn(
                  "border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/50",
                  htrClass,
                )}
              >
                {columns.map((col, index) => {
                  const isLeftSticky = col.sticky === "left";
                  const isRightSticky = col.sticky === "right";

                  return (
                    <th
                      key={col.key}
                      className={cn(
                        "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400",

                        col.headerNoWrap !== false && "whitespace-nowrap",
                        hasFixedColumns && "overflow-hidden",

                        // Sticky background
                        col.sticky && "bg-slate-50 dark:bg-slate-800",

                        // Left sticky border
                        isLeftSticky &&
                          "shadow-[1px_0_0_0_theme(colors.slate.200)] dark:shadow-[1px_0_0_0_theme(colors.slate.700)]",

                        // Right sticky border
                        isRightSticky &&
                          "shadow-[-1px_0_0_0_theme(colors.slate.200)] dark:shadow-[-1px_0_0_0_theme(colors.slate.700)]",

                        col.thClass,
                        thClass,
                      )}
                      style={getStickyStyles(col, index, true)}
                    >
                      {typeof col.label === "function"
                        ? col.label()
                        : col.label}
                    </th>
                  );
                })}
              </tr>
            </thead>

            {/* Body */}
            {showTable && (
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {data.map((row, rowIndex) => (
                  <React.Fragment key={row?.id ?? rowIndex}>
                    <motion.tr
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{
                        duration: 0.18,
                        delay: rowIndex * 0.02,
                      }}
                      className={cn(
                        "transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/40",
                        trClass,
                      )}
                    >
                      {columns.map((col, colIndex) => {
                        const isLeftSticky = col.sticky === "left";
                        const isRightSticky = col.sticky === "right";
                        const shouldClipCell =
                            col.clip !== false &&
                            (hasFixedColumns ||
                                col.width != null ||
                                col.minWidth != null ||
                                col.ellipsis === true);
                        const cellContent = col.render
                            ? col.render(row, rowIndex)
                            : (row?.[col.key] ?? "-");

                        const cellTooltip =
                            typeof col.getTooltip === "function"
                                ? col.getTooltip(row, rowIndex)
                                : typeof cellContent === "string"
                                  ? cellContent
                                  : "";
                        const cellTooltipText =
                            cellTooltip != null && cellTooltip !== "" && cellTooltip !== "-"
                                ? String(cellTooltip)
                                : undefined;

                        return (
                          <td
                            key={col.key}
                            title={cellTooltipText}
                            className={cn(
                              "px-4 py-3 text-sm text-slate-700 dark:text-slate-300",

                              shouldClipCell && "max-w-0 overflow-hidden",

                              // Sticky background
                              col.sticky && "bg-white dark:bg-slate-900",

                              // Left sticky divider
                              isLeftSticky &&
                                "shadow-[1px_0_0_0_theme(colors.slate.200)] dark:shadow-[1px_0_0_0_theme(colors.slate.700)]",

                              // Right sticky divider
                              isRightSticky &&
                                "shadow-[-1px_0_0_0_theme(colors.slate.200)] dark:shadow-[-1px_0_0_0_theme(colors.slate.700)]",

                              col.tdClass,
                              tdClass,
                            )}
                            style={getStickyStyles(col, colIndex, false)}
                          >
                            {shouldClipCell ? (
                              <div
                                className={cn(
                                  "min-w-0 max-w-full overflow-hidden",
                                  col.tdClass?.includes("text-center") && "text-center",
                                  col.tdClass?.includes("text-right") && "text-right",
                                )}
                              >
                                {cellContent}
                              </div>
                            ) : (
                              cellContent
                            )}
                          </td>
                        );
                      })}
                    </motion.tr>

                    {ExpandedRow ? ExpandedRow(row, rowIndex) : null}
                  </React.Fragment>
                ))}
              </tbody>
            )}
          </table>
        </div>
      </div>

      {/* Empty State */}
      {isEmpty && !loading && (
        <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
            <Icon className="h-8 w-8 text-slate-400" />
          </div>

          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            {emptyText}
          </h3>

          {isAplyingFilter && (
            <>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                {subText}
              </p>

              <button
                onClick={resetFilters}
                className="mt-5 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-teal-700"
              >
                Clear Filters
              </button>
            </>
          )}
        </div>
      )}

      {/* Loading State */}
      {isEmpty && loading && (
        <div className="flex items-center justify-center px-6 py-16">
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
            Loading...
          </p>
        </div>
      )}
    </div>
  );
};

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
  DataTable,
};
